/**
 * Homeflix Playback & Diagnostic Logger
 * Comprehensive tracing for video playback, stream lifecycle, episode switches, and errors.
 *
 * Debug tip: In browser console, run:
 *   __homeflixLogs.dump()   — pretty-print all logs
 *   __homeflixLogs.get()    — get raw log array
 *   __homeflixLogs.clear()  — clear all logs
 */

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
export type LogCategory =
  | 'CAROUSEL'
  | 'ONLINE_STREAM'
  | 'VIDEO_PLAYER'
  | 'EPISODE_SWITCH'  // Tracks all movie/episode transitions (auto or manual)
  | 'MEDIA_NAV'
  | 'WATCH_PARTY'
  | 'NETWORK'
  | 'SYSTEM';

export interface PlaybackLogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  category: LogCategory;
  event: string;
  details?: Record<string, any>;
}

const MAX_STORED_LOGS = 500;
const STORAGE_KEY = 'homeflix_playback_logs';

class PlaybackLogger {
  private logs: PlaybackLogEntry[] = [];
  private batchQueue: PlaybackLogEntry[] = [];
  private flushTimer: any = null;

  constructor() {
    this.loadFromStorage();
    if (typeof window !== 'undefined') {
      (window as any).__homeflixLogger = this;
      (window as any).__homeflixLogs = {
        get: () => this.getLogs(),
        clear: () => this.clear(),
        dump: () => console.table(this.getLogs()),
      };
    }
  }

  private loadFromStorage() {
    if (typeof window === 'undefined') return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        this.logs = JSON.parse(raw);
      }
    } catch {
      this.logs = [];
    }
  }

  private saveToStorage() {
    if (typeof window === 'undefined') return;
    try {
      // Keep only the most recent MAX_STORED_LOGS entries
      if (this.logs.length > MAX_STORED_LOGS) {
        this.logs = this.logs.slice(-MAX_STORED_LOGS);
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logs));
    } catch {}
  }

  public log(
    level: LogLevel,
    category: LogCategory,
    event: string,
    details?: Record<string, any>,
  ) {
    const entry: PlaybackLogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      timestamp: new Date().toISOString(),
      level,
      category,
      event,
      details,
    };

    this.logs.push(entry);
    this.saveToStorage();

    // Console output with color-coded prefix
    const colors: Record<LogLevel, string> = {
      DEBUG: 'color: #94a3b8',
      INFO: 'color: #38bdf8; font-weight: bold;',
      WARN: 'color: #facc15; font-weight: bold;',
      ERROR: 'color: #f87171; font-weight: bold;',
    };

    console.log(
      `%c[${entry.timestamp.split('T')[1]?.slice(0, 8)}] [${level}] [${category}] ${event}`,
      colors[level],
      details || '',
    );

    // Queue for backend transmission
    this.batchQueue.push(entry);
    this.scheduleFlush(level === 'ERROR' || level === 'WARN');
  }

  public debug(category: LogCategory, event: string, details?: Record<string, any>) {
    this.log('DEBUG', category, event, details);
  }

  public info(category: LogCategory, event: string, details?: Record<string, any>) {
    this.log('INFO', category, event, details);
  }

  public warn(category: LogCategory, event: string, details?: Record<string, any>) {
    this.log('WARN', category, event, details);
  }

  public error(category: LogCategory, event: string, details?: Record<string, any>) {
    this.log('ERROR', category, event, details);
  }

  public getLogs(filter?: { category?: LogCategory; level?: LogLevel; limit?: number }): PlaybackLogEntry[] {
    let result = [...this.logs];
    if (filter?.category) {
      result = result.filter((l) => l.category === filter.category);
    }
    if (filter?.level) {
      result = result.filter((l) => l.level === filter.level);
    }
    if (filter?.limit) {
      result = result.slice(-filter.limit);
    }
    return result;
  }

  public clear() {
    this.logs = [];
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
  }

  /** Summarize counts per category/level for quick diagnostics */
  public getStats() {
    const stats: Record<string, { total: number; errors: number; warns: number }> = {};
    for (const log of this.logs) {
      if (!stats[log.category]) stats[log.category] = { total: 0, errors: 0, warns: 0 };
      stats[log.category]!.total++;
      if (log.level === 'ERROR') stats[log.category]!.errors++;
      if (log.level === 'WARN') stats[log.category]!.warns++;
    }
    return stats;
  }

  /** Get only episode switch events for audit */
  public getEpisodeSwitches() {
    return this.logs.filter((l) => l.category === 'EPISODE_SWITCH');
  }

  private scheduleFlush(immediate = false) {
    if (typeof window === 'undefined') return;
    if (immediate) {
      if (this.flushTimer) clearTimeout(this.flushTimer);
      this.flushToServer();
      return;
    }
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => {
        this.flushTimer = null;
        this.flushToServer();
      }, 4000);
    }
  }

  private async flushToServer() {
    if (this.batchQueue.length === 0) return;
    const batch = [...this.batchQueue];
    this.batchQueue = [];

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('omflix_token') : null;
      await fetch('/api/v1/logs/playback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ logs: batch }),
      });
    } catch {
      // If server unreachable, put back in queue up to limit
      this.batchQueue = [...batch.slice(-50), ...this.batchQueue].slice(-100);
    }
  }
}

export const playbackLogger = new PlaybackLogger();
